/**
 * CV Parser Service
 * Extracts text content from various CV file formats
 */

/**
 * Extract text from a PDF file
 * @param {File} file - PDF file
 * @returns {Promise<string>} Extracted text
 */
async function extractTextFromPDF(file) {
  try {
    // Use pdf.js library for PDF parsing
    const pdfjsLib = window.pdfjsLib;
    if (!pdfjsLib) {
      throw new Error('PDF.js library not loaded');
    }

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    let fullText = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(' ');
      fullText += pageText + '\n';
    }

    return fullText.trim();
  } catch (error) {
    console.error('PDF extraction error:', error);
    throw new Error('Failed to extract text from PDF');
  }
}

/**
 * Extract text from a plain text file
 * @param {File} file - Text file
 * @returns {Promise<string>} File content
 */
async function extractTextFromTXT(file) {
  return await file.text();
}

/**
 * Extract text from DOCX file
 * @param {File} file - DOCX file
 * @returns {Promise<string>} Extracted text
 */
async function extractTextFromDOCX(file) {
  try {
    // Use mammoth.js for DOCX parsing
    const mammoth = window.mammoth;
    if (!mammoth) {
      throw new Error('Mammoth.js library not loaded');
    }

    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value.trim();
  } catch (error) {
    console.error('DOCX extraction error:', error);
    throw new Error('Failed to extract text from DOCX');
  }
}

/**
 * Main function to extract text from CV files
 * @param {File} file - CV file (PDF, DOCX, DOC, TXT, RTF)
 * @returns {Promise<string>} Extracted text content
 */
export async function extractCVText(file) {
  if (!file) {
    throw new Error('No file provided');
  }

  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  try {
    // Handle different file types
    if (fileType.includes('pdf') || fileName.endsWith('.pdf')) {
      return await extractTextFromPDF(file);
    } else if (fileType.includes('wordprocessingml') || fileName.endsWith('.docx')) {
      return await extractTextFromDOCX(file);
    } else if (fileType.includes('msword') || fileName.endsWith('.doc')) {
      // For older .doc files, try to read as text (limited support)
      console.warn('.doc format has limited support. Please use .docx or .pdf for best results.');
      return await extractTextFromTXT(file);
    } else if (fileType.includes('text') || fileName.endsWith('.txt') || fileName.endsWith('.rtf')) {
      return await extractTextFromTXT(file);
    } else {
      throw new Error(`Unsupported file type: ${fileType || 'unknown'}. Please upload PDF, DOCX, or TXT files.`);
    }
  } catch (error) {
    console.error('CV text extraction error:', error);
    throw error;
  }
}

/**
 * Validate CV file size and type
 * @param {File} file - CV file
 * @param {number} maxSizeMB - Maximum file size in MB (default: 10)
 * @returns {boolean} True if valid
 */
export function validateCVFile(file, maxSizeMB = 10) {
  if (!file) {
    throw new Error('No file provided');
  }

  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    throw new Error(`File size exceeds ${maxSizeMB}MB limit`);
  }

  const allowedExtensions = ['.pdf', '.docx', '.doc', '.txt', '.rtf'];
  const fileName = file.name.toLowerCase();
  const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

  if (!hasValidExtension) {
    throw new Error('Invalid file type. Please upload PDF, DOCX, DOC, TXT, or RTF files.');
  }

  return true;
}

export default {
  extractCVText,
  validateCVFile
};
